import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountWorkflowStatus, TableFiltersContainer, withStyles } from 'vopay';
import { Button } from 'UI';
import type { AccountsListing } from './useAccountsListing';
import useAccountFilterFormConfig, { AccountChartViewFilter } from './useAccountFilterFormConfig';
import styles from './AccountFilters.module.scss';

const cn = withStyles(styles);

type AccountFiltersProps = {
  listing: Pick<AccountsListing, 'filters' | 'filtersApplied' | 'applyFilters'>;
  /** The statuses the Status filter offers, in order: the overview's stage tiles (ID-13964 D3). */
  statuses: AccountWorkflowStatus[];
  /** Shares the Hide / Show Filters row, on its left: the partner tab's active count. */
  summary?: ReactNode;
  /** Shares the Hide / Show Filters row, beside the trigger: the Accounts page's View Chart link. */
  actions?: ReactNode;
  /** Set while the page shows its chart (ID-14095): adds the Show Partner Accounts checkbox and rests the Country filter. */
  chartView?: AccountChartViewFilter;
  /** Set when the filters sit in a card of their own: the card's padding replaces the space they keep below them. */
  isInCard?: boolean;
};

/** The account list's Country / Currency / Status / Keyword filters, behind the Hide / Show Filters toggle. */
export default function AccountFilters({
  listing: { filters, filtersApplied, applyFilters },
  statuses,
  summary,
  actions,
  chartView,
  isInCard,
}: AccountFiltersProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.List.SearchForm' });

  const { ctx, renderFormElements } = useAccountFilterFormConfig({
    initialValues: {
      Country: filters.country,
      Currency: filters.currency,
      Status: filters.status,
      SearchText: filters.searchText,
    },
    statuses,
    chartView,
    onSubmit: (values) =>
      applyFilters({
        Country: values.Country,
        Currency: values.Currency,
        VopayAccountStatus: values.Status,
        SearchText: values.SearchText,
      }),
  });

  return (
    <TableFiltersContainer filtersApplied={filtersApplied} className={cn(isInCard && 'inCard')}>
      {summary !== undefined || actions !== undefined ? (
        <div className={cn('triggerRow')}>
          {summary}

          {/* Figma `Control`: the links sit together on the right. */}
          <div className={cn('triggerActions')}>
            {actions}
            <TableFiltersContainer.Trigger />
          </div>
        </div>
      ) : (
        <TableFiltersContainer.Trigger />
      )}

      <TableFiltersContainer.Content>
        <form onSubmit={ctx.handleSubmit} className={cn('searchArea', isInCard && 'inCard', chartView && 'chartView')}>
          {renderFormElements()}

          <Button id="account-transactions-search" type="submit" colorScheme="primary" size="small">
            {t('searchButton', { defaultValue: 'Search' })}
          </Button>
        </form>
      </TableFiltersContainer.Content>
    </TableFiltersContainer>
  );
}
